import type { IconName } from '@fortawesome/fontawesome-svg-core';
import {
  Grid2,
  List,
  ListItemText,
  Stack,
  Typography,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

const messages = defineMessages({
  title: {
    id: 'deviceInfo.title',
    defaultMessage: 'What data comes from connecting my health devices?',
  },
  subtitle: {
    id: 'deviceInfo.subtitle',
    defaultMessage:
      'Data comes from your devices in the following categories. The specific metrics are dependent on the manufacturer. Some examples for each category are listed.',
  },
});

export default function DeviceInfoModalContent(_props: {
  onClose: () => void;
}) {
  const { formatMessage } = useIntl();

  const deviceMetrics: {
    icon: IconName;
    category: string;
    description: string;
    metrics: string[];
  }[] = [
    {
      icon: 'chart-simple',
      category: 'deviceInfo.category.dailyStats',
      description: 'deviceInfo.description.dailyStats',
      metrics: [
        'vitalSigns.avgHeartRate',
        'vitalSigns.distance',
        'deviceInfo.metric.calories',
        'vitalSigns.restingHeartRate',
        'vitalSigns.steps',
      ],
    },
    {
      icon: 'person',
      category: 'deviceInfo.category.body',
      description: 'deviceInfo.description.body',
      metrics: [
        'vitalSigns.bloodPressure',
        'vitalSigns.bodyTemperature',
        'vitalSigns.heartRate',
        'deviceInfo.metric.glucose',
        'vitalSigns.oxygenSaturation',
      ],
    },
    {
      icon: 'snooze',
      category: 'deviceInfo.category.sleep',
      description: 'deviceInfo.description.sleep',
      metrics: [
        'vitalSigns.avgHeartRate',
        'vitalSigns.sleepDuration',
        'vitalSigns.sleepScore',
      ],
    },
    {
      icon: 'person-running-fast',
      category: 'deviceInfo.category.activities',
      description: 'deviceInfo.description.activity',
      metrics: [
        'vitalSigns.avgHeartRate',
        'vitalSigns.distance',
        'deviceInfo.metric.calories',
        'vitalSigns.steps',
      ],
    },
  ];

  return (
    <Stack p={2} spacing={2}>
      <Typography variant="body1">{formatMessage(messages.title)}</Typography>
      <Typography color="text.secondary" variant="body2">
        {formatMessage(messages.subtitle)}
      </Typography>

      <Grid2 container>
        {deviceMetrics.map(({ icon, category, description, metrics }) => {
          return (
            <Grid2
              key={`device-info-${category}`}
              size={{ md: 6, sm: 6, xs: 12 }}
            >
              <List dense sx={{ width: '100%' }}>
                <ListItem alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 32, mt: 1.25 }}>
                    <Icon color="secondary.main" name={icon} />
                  </ListItemIcon>
                  <ListItemText
                    primary={formatMessage({ id: category })}
                    primaryTypographyProps={{
                      variant: 'body1',
                    }}
                    secondary={formatMessage({ id: description })}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      color: 'text.secondary',
                    }}
                  />
                </ListItem>
                {metrics.map((metric) => (
                  <ListItem
                    disablePadding
                    key={`device-info-${metric}`}
                    sx={{ ml: 6, minHeight: 'auto' }}
                  >
                    <ListItemText
                      primary={formatMessage({ id: metric })}
                      primaryTypographyProps={{
                        variant: 'body2',
                        color: 'text.secondary',
                      }}
                      sx={{ m: 0 }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid2>
          );
        })}
      </Grid2>
    </Stack>
  );
}
